import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle, Clock, ArrowLeftRight, Mail } from "lucide-react";
import { motion } from "framer-motion";
import zitiuLogo from "@/assets/zitiu-logo-black.png";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { parseEdgeFunctionError } from "@/lib/parseEdgeFunctionError";
import { rememberActiveWorkspace } from "@/lib/rememberActiveWorkspace";
import * as Sentry from "@sentry/react";
import { z } from "zod";

const signupSchema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const signinSchema = z.object({
  password: z.string().min(1, "Password is required"),
});

type InviteState =
  | { status: "loading" }
  | { status: "form"; mode: "signup" | "signin"; email: string; workspace_name: string; role: string }
  | { status: "accepting" }
  | { status: "accepted"; workspace_name: string }
  | { status: "expired" }
  | { status: "invalid" }
  | { status: "email_mismatch"; invited_email: string; current_email: string }
  | { status: "email_confirmation"; email: string }
  | { status: "error"; message: string };

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading, signUp, signIn, signOut } = useAuth();
  const [state, setState] = useState<InviteState>({ status: "loading" });
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [isCheckingConfirmation, setIsCheckingConfirmation] = useState(false);
  const [confirmationHint, setConfirmationHint] = useState<string | null>(null);
  // Persists invite metadata across state transitions (form -> accepting -> accepted)
  const [inviteInfo, setInviteInfo] = useState<{
    email: string;
    workspace_id: string;
    workspace_name: string;
    role: string;
    mode: "signup" | "signin";
  } | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      setState({ status: "invalid" });
      return;
    }
    if (!user) {
      validateInvitation(token);
    } else {
      acceptInvitation(token);
    }
  }, [token, authLoading, user]);

  // Hold signIn in a ref so the polling effect doesn't re-create its interval
  // every time AuthContext re-renders (e.g. TOKEN_REFRESHED) and hands us a
  // new function identity.
  const signInRef = useRef(signIn);
  signInRef.current = signIn;

  // Prevents overlapping in-flight poll calls if a previous attempt hasn't
  // resolved before the next tick. Dedupe key for Sentry reports so a
  // persistent non-pending error doesn't spam telemetry every 10s.
  const pollInFlightRef = useRef(false);
  const lastReportedSignInErrRef = useRef<string | null>(null);

  const POLL_INTERVAL_MS = 10_000;
  const MAX_POLL_ATTEMPTS = 30; // ~5 minutes — stops hammering indefinitely

  function classifySignInError(error: { code?: string; message?: string }): {
    isPending: boolean;
    key: string;
    message: string;
  } {
    const code = error.code ?? "";
    const message = error.message ?? "";
    const isPending =
      code === "email_not_confirmed" ||
      message.toLowerCase().includes("not confirmed");
    return { isPending, key: code || message || "unknown", message };
  }

  // While the tab is stuck on "check your email" (e.g. user clicked the
  // confirmation link in a different browser), retry sign-in in the
  // background. Supabase returns `email_not_confirmed` until the link is
  // clicked anywhere; once it succeeds, onAuthStateChange fires SIGNED_IN
  // and the outer useEffect advances the flow.
  useEffect(() => {
    if (state.status !== "email_confirmation") return;
    if (!password) return;

    let cancelled = false;
    let attemptCount = 0;
    lastReportedSignInErrRef.current = null;

    const attempt = async () => {
      if (cancelled || pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const { error } = await signInRef.current(state.email, password);
        if (cancelled) return;
        if (!error) return;

        const { isPending, key, message } = classifySignInError(error);
        if (isPending) {
          setConfirmationHint("Still waiting for confirmation — check your inbox and spam folder.");
          return;
        }

        if (key !== lastReportedSignInErrRef.current) {
          Sentry.captureException(error);
          lastReportedSignInErrRef.current = key;
        }
        console.error("Confirmation poll sign-in failed:", error);
        setConfirmationHint(message || "Couldn't sign you in yet. Retrying…");
      } catch (err) {
        if (cancelled) return;
        console.error("Confirmation poll sign-in threw:", err);
        const name = err instanceof Error ? err.name : "UnknownError";
        if (name !== lastReportedSignInErrRef.current) {
          Sentry.captureException(err);
          lastReportedSignInErrRef.current = name;
        }
        setConfirmationHint("Couldn't reach the server — check your connection. We'll keep trying.");
      } finally {
        pollInFlightRef.current = false;
      }
    };

    const interval = window.setInterval(() => {
      attemptCount += 1;
      if (attemptCount > MAX_POLL_ATTEMPTS) {
        window.clearInterval(interval);
        setConfirmationHint("Auto-check paused after 5 minutes. Tap the button below to retry.");
        return;
      }
      void attempt();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [state.status, state.email, password]);

  async function handleManualConfirmCheck(): Promise<void> {
    if (state.status !== "email_confirmation" || !password) return;
    setIsCheckingConfirmation(true);
    setConfirmationHint(null);
    try {
      const { error } = await signInRef.current(state.email, password);
      if (!error) return;
      const { isPending, message } = classifySignInError(error);
      setConfirmationHint(
        isPending
          ? "Still waiting for confirmation — check your inbox and spam folder."
          : message || "Couldn't sign you in yet. Please try again.",
      );
      if (!isPending) {
        Sentry.captureException(error);
      }
    } catch (err) {
      console.error("Manual confirmation sign-in threw:", err);
      Sentry.captureException(err);
      setConfirmationHint("Couldn't reach the server — check your connection.");
    } finally {
      setIsCheckingConfirmation(false);
    }
  }

  async function validateInvitation(token: string): Promise<void> {
    try {
      const response = await supabase.functions.invoke("validate-invitation", {
        body: { token },
      });

      if (response.error) {
        console.error("validate-invitation error:", response.error);
        Sentry.captureException(response.error);
        setState({ status: "error", message: "Could not validate invitation. Please try again." });
        return;
      }

      const data = response.data;
      if (!data?.ok) {
        setState({ status: data?.error === "expired" ? "expired" : "invalid" });
        return;
      }

      const mode = data.has_account ? "signin" : "signup";
      const info = {
        email: data.email,
        workspace_id: data.workspace_id,
        workspace_name: data.workspace_name,
        role: data.role,
        mode,
      };
      setInviteInfo(info);
      setState({ status: "form", ...info });
    } catch (err) {
      console.error("Failed to validate invitation:", err);
      Sentry.captureException(err);
      setState({ status: "error", message: "Something went wrong. Please try again." });
    }
  }

  async function acceptInvitation(token: string): Promise<void> {
    try {
      const response = await supabase.functions.invoke("accept-invitation", {
        body: { token },
      });

      if (response.error) {
        await handleAcceptError(response.error);
        return;
      }

      if (user && response.data?.workspace_id) {
        rememberActiveWorkspace(user.id, response.data.workspace_id);
      }
      setState({
        status: "accepted",
        workspace_name: response.data.workspace_name || "the workspace",
      });
    } catch (err) {
      console.error("Failed to accept invitation:", err);
      Sentry.captureException(err);
      setState({ status: "error", message: "Something went wrong. Please try again." });
    }
  }

  async function handleAcceptError(error: any): Promise<void> {
    const body = await parseEdgeFunctionError(error);
    const errMsg = (body.error as string) || error.message || "";
    const status = error?.context?.status as number | undefined;

    // Handle auth failures by status code first so AuthError messages like
    // "Invalid JWT" don't get misrouted into the "invalid invitation" branch.
    if (status === 401) {
      setState({ status: "error", message: "Your session expired. Please sign in again." });
      return;
    }

    if (body.error === "email_mismatch") {
      setState({
        status: "email_mismatch",
        invited_email: body.invited_email as string,
        current_email: body.current_email as string,
      });
    } else if (errMsg.includes("already been")) {
      // handle_new_user() trigger already accepted -- treat as success.
      // accept-invitation returns workspace_id even in the 410 response so we can
      // still switch the user into the invited workspace; fall back to inviteInfo
      // (populated only for the unauthenticated-validate path) if the field is absent.
      const workspaceId = (body.workspace_id as string) || inviteInfo?.workspace_id;
      if (user && workspaceId) {
        rememberActiveWorkspace(user.id, workspaceId);
      }
      setState({ status: "accepted", workspace_name: inviteInfo?.workspace_name || "the workspace" });
    } else if (errMsg.includes("expired")) {
      setState({ status: "expired" });
    } else if (errMsg.toLowerCase().includes("invalid")) {
      setState({ status: "invalid" });
    } else {
      console.error("accept-invitation error:", error, "parsed body:", body);
      setState({ status: "error", message: errMsg || "Something went wrong" });
    }
  }

  /**
   * Shared submit handler for both signup and signin forms.
   * After auth succeeds, onAuthStateChange fires -> user becomes non-null ->
   * useEffect re-runs -> acceptInvitation is called -> shows "accepted" state.
   */
  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (state.status !== "form") return;

    const isSignup = state.mode === "signup";
    const schema = isSignup ? signupSchema : signinSchema;
    const validation = schema.safeParse(isSignup ? { fullName, password } : { password });
    if (!validation.success) {
      setFormError(validation.error.errors[0].message);
      return;
    }

    setFormError("");
    setIsSubmitting(true);
    setState({ status: "accepting" });

    if (isSignup) {
      const { error, session } = await signUp(state.email, password, fullName);
      if (error) {
        setFormError(error.message);
        setIsSubmitting(false);
        if (inviteInfo) setState({ status: "form", ...inviteInfo });
        return;
      }
      // No session means email confirmation is required.
      // The handle_new_user() trigger has already accepted the invitation,
      // so after confirming email the user can just log in.
      if (!session) {
        setIsSubmitting(false);
        setState({ status: "email_confirmation", email: state.email });
        return;
      }
    } else {
      const { error } = await signIn(state.email, password);
      if (error) {
        setFormError(error.message);
        setIsSubmitting(false);
        if (inviteInfo) setState({ status: "form", ...inviteInfo });
        return;
      }
    }
    // For signin (or auto-confirmed signup), onAuthStateChange will fire
    // SIGNED_IN → user becomes non-null → useEffect calls acceptInvitation().
  }

  function handleGoToApp(): void {
    navigate("/app/studio", { replace: true });
  }

  if (authLoading || state.status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 gradient-glow opacity-30" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="flex items-center justify-center mb-8">
          <img src={zitiuLogo} alt="Zitiu" className="h-8 w-auto" />
        </div>

        <Card className="glass">
          <CardHeader className="text-center">
            <StatusHeader state={state} />
          </CardHeader>

          <CardContent className="space-y-3">
            {state.status === "form" && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={state.email}
                    readOnly
                    autoComplete="username"
                    className="bg-muted border-border"
                  />
                </div>

                {state.mode === "signup" && (
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Doe"
                      className="bg-secondary border-border"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={isSubmitting}
                      autoComplete="name"
                      autoFocus
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={state.mode === "signup" ? "At least 6 characters" : "Enter your password"}
                    className="bg-secondary border-border"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete={state.mode === "signup" ? "new-password" : "current-password"}
                    autoFocus={state.mode === "signin"}
                  />
                </div>

                {formError && (
                  <p className="text-sm text-destructive">{formError}</p>
                )}

                <Button variant="hero" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {state.mode === "signup" ? "Creating account..." : "Signing in..."}
                    </>
                  ) : (
                    state.mode === "signup" ? "Create account & join" : "Sign in & join"
                  )}
                </Button>

                {state.mode === "signup" ? (
                  <p className="text-center text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Link to={`/login?redirect=/invite/${token}`} className="text-primary hover:underline font-medium">
                      Sign in
                    </Link>
                  </p>
                ) : (
                  <p className="text-center text-sm text-muted-foreground">
                    Don't have an account?{" "}
                    <Link to="/signup" className="text-primary hover:underline font-medium">
                      Create account
                    </Link>
                  </p>
                )}
              </form>
            )}

            {state.status === "accepted" && (
              <Button variant="hero" className="w-full" onClick={handleGoToApp}>
                Go to workspace
              </Button>
            )}

            {state.status === "email_mismatch" && (
              <div className="space-y-4">
                <div className="rounded-lg border border-border divide-y divide-border overflow-hidden">
                  <div className="px-3 py-2.5 bg-secondary/40">
                    <p className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground mb-0.5">
                      Invited
                    </p>
                    <p className="font-mono text-sm break-all">{state.invited_email}</p>
                  </div>
                  <div className="px-3 py-2.5">
                    <p className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground mb-0.5">
                      Currently signed in as
                    </p>
                    <p className="font-mono text-sm break-all text-muted-foreground">{state.current_email}</p>
                  </div>
                </div>

                <Button variant="hero" className="w-full" onClick={() => signOut()}>
                  Sign out &amp; continue
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  You'll be signed out and returned here to continue.
                </p>
              </div>
            )}

            {state.status === "email_confirmation" && (
              <div className="space-y-3">
                {password ? (
                  <>
                    <Button
                      variant="hero"
                      className="w-full"
                      onClick={handleManualConfirmCheck}
                      disabled={isCheckingConfirmation}
                    >
                      {isCheckingConfirmation ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Checking…
                        </>
                      ) : (
                        "I've confirmed my email"
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      We check automatically every 10 seconds.
                    </p>
                    {confirmationHint && (
                      <p className="text-xs text-center text-muted-foreground">{confirmationHint}</p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-center text-muted-foreground">
                    Reloading the page cleared your session. Click the link in
                    your email to activate your account, then sign in below.
                  </p>
                )}
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/login">Go to login instead</Link>
                </Button>
              </div>
            )}

            {(state.status === "expired" || state.status === "invalid" || state.status === "error") && (
              <Button variant="outline" className="w-full" asChild>
                <Link to="/login">Go to login</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

/** Renders the card header icon, title, and description based on invite state */
function StatusHeader({ state }: { state: InviteState }): JSX.Element | null {
  switch (state.status) {
    case "form":
      return (
        <>
          <CardTitle className="font-display text-xl">
            You've been invited to {state.workspace_name}
          </CardTitle>
          <CardDescription>
            {state.mode === "signup"
              ? `Create an account to join as ${state.role}`
              : "Sign in to accept your invitation"}
          </CardDescription>
        </>
      );
    case "accepting":
      return (
        <>
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
          <CardTitle className="font-display text-xl">Accepting invitation...</CardTitle>
        </>
      );
    case "accepted":
      return (
        <>
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
          <CardTitle className="font-display text-xl">Welcome!</CardTitle>
          <CardDescription>You've joined {state.workspace_name}</CardDescription>
        </>
      );
    case "email_confirmation":
      return (
        <>
          <Mail className="w-12 h-12 text-primary mx-auto mb-2" />
          <CardTitle className="font-display text-xl">Check your email</CardTitle>
          <CardDescription>
            We've sent a confirmation link to <strong>{state.email}</strong>.
            Click the link and we'll sign you in here automatically — even if
            you open it in a different browser.
          </CardDescription>
        </>
      );
    case "expired":
      return (
        <>
          <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
          <CardTitle className="font-display text-xl">Invitation expired</CardTitle>
          <CardDescription>
            This invitation link has expired. Please ask the workspace admin to send a new one.
          </CardDescription>
        </>
      );
    case "invalid":
      return (
        <>
          <XCircle className="w-12 h-12 text-destructive mx-auto mb-2" />
          <CardTitle className="font-display text-xl">Invalid invitation</CardTitle>
          <CardDescription>
            This invitation link is not valid. It may have been revoked or already used.
          </CardDescription>
        </>
      );
    case "email_mismatch":
      return (
        <>
          <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto mb-2">
            <ArrowLeftRight className="w-5 h-5 text-muted-foreground" />
          </div>
          <CardTitle className="font-display text-xl">Switch account to continue</CardTitle>
          <CardDescription>
            This invitation belongs to a different email than the one you're signed in with.
          </CardDescription>
        </>
      );
    case "error":
      return (
        <>
          <XCircle className="w-12 h-12 text-destructive mx-auto mb-2" />
          <CardTitle className="font-display text-xl">Something went wrong</CardTitle>
          <CardDescription>{state.message}</CardDescription>
        </>
      );
    default:
      return null;
  }
}

